/**
 * The **authored seed overlay entries** — **INTRINSICS-ONLY**. Real CONTENT entries
 * (weapon/core-bonus/system/talent-rank ids and their name-keyed status markers) live in
 * `packs/core-rulebook`'s sidecar (see that pack's `entries/` for the entry modules +
 * entry tests). What remains here is the ENGINE-INTRINSIC catalog only: the universal
 * status/condition catalog (Impaired, Lock On, Bolster, Shredded, Exposed, Braced, Stunned, Immobilized,
 * Flying, Grappled, Slowed, Prone, Jammed, Engaged, Danger Zone, Cascading, Down and Out,
 * Shut Down, Biological, Hidden, Invisible, Intangible), the generic `act_*` combat actions
 * (Invade, Ram, Grapple, Lock On, Bolster, Scan), and the parameterized `tg_*` tag
 * templates (AP, Accurate {VAL}, Reliable {VAL}, Overkill, Limited {VAL}, Knockback
 * {VAL}) — none of these are LCP-shipped content, so they stay engine-side per the
 * `INTRINSIC_STATUS_NAMES`/`INTRINSIC_ACTION_PREFIX` boundary (`sidecar.ts`). Braced /
 * Flying / Grappled are intrinsic by extension of that boundary (their planting
 * actions — `act_brace`, generic `apply_status`, `act_grapple` — are themselves
 * intrinsic, so their bespoke status payloads stay alongside them rather than
 * splitting a planter/marker pair across the intrinsic/content line).
 */

import type { OverlayEntry } from "./atoms";
import { VAL } from "./pack";

// ── core statuses (modeled as modifier entities) ───────────────────────────────────

/**
 * **Impaired** — RAW Core p.78: "+1 Difficulty on all attacks, saves, and checks."
 * `attackerAccuracy: -1` folds the attack side; `saveCheckBonus: -1` folds the save/check
 * side — **persistent, no `consumeOnSaveCheck`** (unlike Bolster's one-shot boon, Impaired's
 * Difficulty rides EVERY save/check while the status is active). Reaches every save/check
 * site through the shared carrier-self `saveCheckBonus` fold: `saves.ts`'s `save_ends`
 * re-save, `contest.ts`'s opposed check, and `interpret.ts`'s `force_save`.
 */
export const IMPAIRED: OverlayEntry = {
  id: "Impaired",
  effects: [{ kind: "modifier", mod: { attackerAccuracy: -1, saveCheckBonus: -1 } }],
};

/** **Lock On** — +1 accuracy to an attacker who consumes it (asked before the roll, `settleConsumeChoice`). */
export const LOCK_ON: OverlayEntry = {
  id: "Lock On",
  effects: [{ kind: "modifier", mod: { targetAccuracy: 1, consumeOnHit: true } }],
};

/**
 * **Bolster** — the buff the BOLSTER quick tech action (`act_bolster`) confers: +2 Accuracy
 * to the carrier's **next save or skill check**, consumed on that save/check (RAW Core p. 69,
 * "+2◆ on the next skill check or save … between now and the end of their next turn …
 * characters can only benefit from one BOLSTER at a time"). A carrier-self modifier folded
 * through the shared `saveCheckBonus` seam every save/check site reaches: `saves.ts`'s
 * `save_ends` re-save, `contest.ts`'s opposed check, and `interpret.ts`'s `force_save`.
 * Modeled as a modifier so the consume + fold ride the existing machinery, exactly like
 * Lock On.
 */
export const BOLSTER: OverlayEntry = {
  id: "Bolster",
  effects: [{ kind: "modifier", mod: { saveCheckBonus: 2, consumeOnSaveCheck: true } }],
};

/**
 * **Shredded** — RAW Core p.78: "don't benefit from ARMOR or RESISTANCE." An attack
 * against the carrier ignores its Armor (`ignoreTargetArmor: true`, folded into the
 * `damage` step's mitigation — the reduced armor is baked into `damage.armor`/`final`)
 * AND gets no Resistance benefit from any source: `overlayIgnoreArmor` also gates the
 * `resistant` computation shut at every mitigate call site, so a Shredded target that Braces
 * still takes the full, unhalved hit. The non-attack damage sites read the same bit through
 * `nonAttackArmor`/`nonAttackResistant` (`nonAttackResistance.ts`), so a Shredded character run
 * over, mined or hit by GM damage loses both too. A *target-side* modifier; emits no step of its own.
 */
export const SHREDDED: OverlayEntry = {
  id: "Shredded",
  effects: [{ kind: "modifier", mod: { ignoreTargetArmor: true } }],
};

/**
 * **Exposed** — the carrier takes **double** kinetic/energy/explosive damage until the
 * status is cleared (applied by the Overheating "Destabilized Power Plant"
 * row). A *target-side* modifier folding into the `damage` step's pre-mitigation total (the
 * doubled damage is baked into `damage.preMitigation`/`final`); emits no step. Burn/heat are
 * not doubled — only the three physical types (the resolver gates the double on `dtype`).
 */
export const EXPOSED: OverlayEntry = {
  id: "Exposed",
  effects: [{ kind: "modifier", mod: { doubleDamage: true } }],
};

/**
 * **Braced** — the durational half of the BRACE reaction (RAW Core p. 73). The *Resistance to
 * the triggering attack* half is injected straight into that attack's mitigation by the
 * resolver's `mitigate` path and is NOT
 * modeled here; this status is the second clause — **"until the end of your next turn, all other
 * attacks against you are made at +1 Difficulty."** A +1 Difficulty to an attacker is −1 net
 * accuracy, so this is a *target-side* `targetAccuracy: -1` modifier (the mirror of Lock On's
 * +1), folding into the attacker's to-hit math exactly like the other status modifiers. The
 * "other attacks" exclusion of the triggering attack is automatic: the trigger's `to_hit` is
 * already recorded before this status is applied, so only *subsequent* attacks read the −1. The
 * instance is added with `target_next_turn_end` expiry (the duration bus removes it at the end
 * of the bracer's — the carrier's — next turn).
 *
 * NOTE (scope): the RAW also says the bracer "cannot take reactions until the end of your next
 * turn" — that clause IS enforced, by `checkReactionStatusLegality` (`statusLegality.ts`) reading
 * this status, whose expiry is exactly that window. It covers the CURRENT turn too (e.g. an
 * Overwatch after the bracer's slot reopens at the next turn start), not just the next. The
 * action-economy penalty on the bracer's next turn (one quick action only, no Overcharge, Full or
 * free actions, no normal move) is enforced off this status too: `checkBudget`'s braced-turn wall,
 * `checkOverchargeLegality`, and the domain's `freshTurnBudget`/`move_token`/Boost walls, all
 * through `onBracedTurn` (`@lancer-vtt/domain`). This overlay entry carries only the to-hit
 * modifier.
 */
export const BRACED: OverlayEntry = {
  id: "Braced",
  effects: [{ kind: "modifier", mod: { targetAccuracy: -1 } }],
};

// ── core advisory statuses ─────────────────────────────────────────────────────────
//
// Slowed / Immobilized carry no attack-math facet, only an `advisory` message raised
// when the carrier ATTACKS — but their movement/action-economy clauses are hard-enforced at
// the move-budget/reducer wall (`voluntaryMoveBar`, the `boost` reducer case, the Prone
// crawl-cost rider, the strict `stand_up` command — see each entry below). Stunned and Prone
// carry real math facets (below), and their can't-act / movement clauses are hard-enforced too.

/**
 * **Stunned** (RAW Core p.78: "characters gain no
 * benefit from Instinct or Speed, and can take no actions… -5 to their EVASION [sic
 * "max 5 EVASION" per errata]… automatically fail all HULL and AGILITY checks and
 * saves"). Two real math facets, both TARGET/carrier-self as appropriate:
 *   - `evasionCap: 5` — a target-side modifier: an attack against the carrier reads
 *     `min(evasion, 5)` at the defense read (`resolve/toHit.ts`'s `defenseFor`),
 *     weapon attacks only (E-DEFENSE is untouched).
 *   - `autoFailChecks: ["hull", "agility"]` — a carrier-self trait folded at every
 *     stat-typed roll site: `interpret.ts`'s `interpretForceSave` (a forced HULL/AGI
 *     save), `contest.ts`'s `resolveContest` (an opposed check whose axis names HULL
 *     or AGI — e.g. a Grapple-break), and `saves.ts`'s `resolveSaveEnds` (a `save_ends`
 *     re-save whose status carries a HULL/AGI `expiry.stat`, checked per instance —
 *     `decisions/0163` D6). Recorded as an honest skip-roll→fail (`SaveStep.
 *     autoFailed`/`ContestStep.*AutoFailed`) — never a fake roll. A bare, axis-less
 *     `save_ends` (no `stat` stamped) names no axis and so never matches.
 * The **can't-act / no-Overcharge** clause is a **hard dispatch-seam wall**:
 * `checkActorStatusLegality` / `checkReactionStatusLegality` (`economy.ts`) key off the
 * shared `isStunned()` predicate (Stunned ∨ Shut Down), rejecting every
 * action/reaction/Overcharge PRE-DICE — EXCEPT the pinned action id `act_boot_up` and,
 * later, the pilot Mount/Dismount/Eject ids. The **no-move** clause is a hard wall too:
 * `voluntaryMoveBar` (`packages/domain/src/encounter/movement.ts`) reads the same
 * `isStunned()` predicate and bars every voluntary `move_token`/Boost for the carrier;
 * involuntary `forced_move` is untouched by construction.
 *
 * **Spreads into Shut Down's composite entry by object reference** (`SHUT_DOWN.effects`
 * below spreads `STUNNED.effects` verbatim): mechanizing these facets here mechanizes Shut
 * Down's Evasion cap + auto-fail for free, no separate authoring — and the wall reads
 * `isStunned()`, so a Shut Down mech hits the identical can't-act wall with zero extra code.
 */
export const STUNNED: OverlayEntry = {
  id: "Stunned",
  effects: [
    {
      kind: "modifier",
      mod: {
        evasionCap: 5,
        autoFailChecks: ["hull", "agility"],
      },
    },
  ],
};

/** **Immobilized** (RAW Core p.78) — no voluntary movement and no Boost are HARD-ENFORCED:
 * `voluntaryMoveBar`
 * (`packages/domain/src/encounter/movement.ts`) bars every voluntary `move_token`,
 * and the domain reducer's `boost` case rejects Boost outright (`reducer.ts`).
 * Involuntary movement (`forced_move`) is unaffected by construction. */
export const IMMOBILIZED: OverlayEntry = {
  id: "Immobilized",
  effects: [],
};

/**
 * **Flying** (RAW Core p.63, FLIGHT). Applied/removed via the generic `apply_status`/`remove_status`
 * commands (`lvtt encounter status add|remove --status Flying`) — content that grants
 * flight (a frame trait, a hover NPC) just lands this status, no bespoke command.
 *
 * The bulk of what FLIGHT grants — pass over occupants/solid-terrain/edge-walls,
 * difficult-terrain immunity (a labelled non-RAW inference) — is HARD-ENFORCED as a
 * movement-context rider, not an overlay facet: see `FLYING_STATUS`/`classifyOccupant`/
 * `movementContext` in `packages/domain/src/encounter/movement.ts`. This entry carries
 * only the one piece that genuinely IS an overlay-immunity fact: **Immunity to Prone**
 * (RAW: a flying character cannot be knocked Prone), which blocks a `status_add: "Prone"`
 * atom at emission (`statusBlockedByImmunity`) — the same mechanism Shut Down/Biological's
 * tech-immunity and Stay of Execution's damage/status-immunity
 * use. (The generic manual-tier `apply_status` command
 * itself does NOT consult this gate — by design, mirroring every other status-catalog
 * entry: the overlay-immunity fold only intercepts the CONTENT path, `interpret.ts`'s
 * `add_status` atom handler, never the GM's raw force-apply verb.)
 */
export const FLYING: OverlayEntry = {
  id: "Flying",
  effects: [
    {
      kind: "modifier",
      mod: {
        immunity: [{ kind: "status", name: "Prone" }],
      },
    },
  ],
};

/**
 * **Grappled** — the invented composite marker the `GRAPPLE` on-hit effect (above) lands on
 * both parties. Unlike a universal-catalog status, `Grappled` is Grapple's own overlay entry;
 * the marker carries only the partner-ref relationship data (`StatusInstance.partner`) plus
 * this advisory. The mutual bind + real Engaged are enforced, and the **no-Boost / no-react
 * half is HARD-ENFORCED** (`checkActorStatusLegality`'s Boost gate — the domain reducer's
 * `boost` case — and `checkReactionStatusLegality`, the same wall Jammed shares). The
 * composite clause ("the smaller becomes IMMOBILIZED and mirrors the larger's movement;
 * same-Size parties contest HULL at turn start for who counts as larger") is hard-enforced
 * too: `grappleDominantOf`/`grappleImmobilizedSyncSteps` stamp the real `Immobilized` on the
 * non-dominant party, and `dragRecomputer` mirrors the dominant party's `move_token` onto the
 * bound partner (mirror → footprint clamp → adjacency-restore). The partner is no obstacle to
 * the dominant party's own move, so it can be pushed and passed over (the domain's
 * `draggedPartnerOf`); the same-Size tiebreak is `request_grapple_contest`/CLI `grapple-contest`.
 */
export const GRAPPLED: OverlayEntry = {
  id: "Grappled",
  effects: [],
};

/** **Slowed** (RAW Core p.78) — no-Boost is HARD-ENFORCED (the domain reducer's `boost` case
 * rejects a Slowed carrier outright).
 * The movement-mode half ("only the standard move, on their own turn — no special moves
 * granted by talents, systems, or weapons") has **no further enforcement surface**:
 * "on their own turn"
 * is vacuously satisfied by `mayPlayMove` (which already restricts PLAY movement to the
 * acting unit universally, Slowed or not — nothing to build); "no special moves" has zero
 * enforcement surface today because no special-move concept exists in the engine — this
 * is the Forward-contract note on `voluntaryMoveBar`
 * (`packages/domain/src/encounter/movement.ts`) for whoever builds the first one. */
export const SLOWED: OverlayEntry = {
  id: "Slowed",
  effects: [],
};

/**
 * **Prone** (RAW Core p.77 — verified against the current rulebook: NO melee/ranged split,
 * no adjacency map needed). "Attacks
 * against a Prone character are made with +1 Accuracy" — a flat `targetAccuracy: 1`,
 * Lock On's mirror, but **not consumed** (Prone doesn't clear on being hit). The
 * carrier's own clauses ("Prone characters are Slowed and count as moving through
 * difficult terrain… may stand in place of a standard move, unless Immobilized —
 * standing isn't movement") are **HARD-ENFORCED**: the crawl-cost rider on `movementContext`'s
 * `moveCost` closure (`Math.max(terrainCost, 2)`, mover-side, Prone-specific — a
 * merely-Slowed, non-Prone mover pays ordinary cost) and the strict `stand_up`
 * reducer command (legal only while the standard move is unspent, rejects while
 * Immobilized per RAW, never touches `pos` so standing can never trigger
 * Overwatch) — both in `packages/domain/src/encounter/movement.ts` /
 * `reducer.ts`. Slowed-inheritance (Prone ⇒ Slowed, so no Boost) rides the catalog's
 * `isSlowed()` composite at the Boost wall.
 */
export const PRONE: OverlayEntry = {
  id: "Prone",
  effects: [
    {
      kind: "modifier",
      mod: {
        targetAccuracy: 1,
      },
    },
  ],
};

// ── the rest of the standard condition/status catalog ──────────────────────────────
//
// The standard Lancer conditions (clearable: caused by damage/EW) and statuses (sticky:
// require a specific action to clear) NOT already seeded above, authored as overlay
// entries so the whole combat-relevant catalog lives in the overlay world. Most of these
// gate *movement*, *targetability*, or *out-of-turn* actions the resolver doesn't model as
// attack math — so they are **advisory modifiers**: warned-on when the carrier attacks,
// never hard-blocking. Where a status has a faithful *attack-math* facet the engine CAN
// fold (Engaged's +1 ranged Difficulty), it is modeled as math; the rest is advisory,
// honest about what's not mechanized. Only the universal combat conditions/statuses live
// here — item/system content is authored in packs.

/**
 * **Jammed** (condition, RAW Core p.78) — electronic-warfare lockout. Every clause but comms
 * is HARD-ENFORCED: no tech actions
 * (`checkActorStatusLegality`'s `tech` branch, actor side) or benefit from them even from
 * an ally (`checkJammedBenefitLegality`, target side); no reactions
 * (`checkReactionStatusLegality`, shared with Grappled); attacks limited to Improvised
 * Attack/Grapple/Ram (`checkActorStatusLegality`'s legal-attack-set). **"No comms" stays
 * advisory** — it's a table-layer/roleplay effect (no in-engine comms channel exists to
 * gate), correctly left un-mechanized.
 */
export const JAMMED: OverlayEntry = {
  id: "Jammed",
  effects: [],
};

/**
 * **Engaged** (status) — a character adjacent to a hostile character. **Ranged**
 * attacks made by an ENGAGED character take **+1 Difficulty** (RAW p.62); melee
 * attacks are unaffected. Modeled as `attackerAccuracy: -1` (a Difficulty is a
 * negative accuracy) gated on `match: { family: "attack", ranged: true }` — the SAME
 * `isRangedAttack(weapon.range)` + `!intent.tech` shape the cover fold uses (`resolve/attackModifiers.ts`'s `foldAttackCover`),
 * so a melee attack while Engaged no longer takes the −1. Fully mechanized — no advisory
 * needed (the melee over-application this entry used to warn about is fixed, not merely
 * flagged).
 */
export const ENGAGED: OverlayEntry = {
  id: "Engaged",
  effects: [
    {
      kind: "modifier",
      match: { family: "attack", ranged: true },
      mod: {
        attackerAccuracy: -1,
      },
    },
  ],
};

/**
 * **Danger Zone** (status) — a mech at or ABOVE half its HEAT CAP (Core p.62: "half or more of
 * their heat is filled in"; the tag glossary p.104: "heat equal to at least half of their HEAT
 * CAP"). Matches the real gate, `predicate.ts`'s `danger_zone` arm (`heat >= maxHeat/2`), and the
 * advisory string below. This comment said "at or below" until 2026-08-31 — inverted, and the
 * frontend's status blurb had copied it (`frontend/src/lib/encounter/statusCatalog.ts`, whose
 * header names these doc comments as its wording source). A state-gate certain
 * weapons/talents read; no attack-roll math of its own. Advisory (informational — the
 * heat-cap test is the real gate, surfaced for the play-by-play).
 */
export const DANGER_ZONE: OverlayEntry = {
  id: "Danger Zone",
  effects: [],
};

/**
 * **Cascading** (RAW Core p.71) — landed by a natural-1 AI
 * cascade check ({@link "../tables".resolveOverheating}/{@link "../tables"
 * .resolveStructureDamage}'s `rollCascadeCheck`, a grounded `status_add` beside the
 * record-only `ai_cascade_check` witness). RAW: the NHP "immediately takes over the mech and
 * becomes an NPC under the control of the GM," and "the only action a pilot in a cascading mech
 * can take" is Shut Down.
 *
 * **MARKER-ONLY, deliberately** — this entry carries NO combat modifier (no attack math, no
 * immunity, no advisory-with-behavior riding it). The "can only Shut Down" restriction is a
 * status-aware branch in `canResolveIntent`/`canIssueEncounter`
 * (`packages/domain/src/permissions.ts`): a non-GM actor acting on a `Cascading` combatant is
 * permitted ONLY `request_shutdown`, every other intent/command hard-denied; the GM's own
 * `isGm` bypass IS the takeover (no hostility/allegiance flip rides this status either).
 * That gate lives in the reducer wall, not here — **do not "helpfully" add a modifier to this
 * entry.** `expiry: { kind: "until_removed" }`, cleared by Shut Down (`shutDownSteps`).
 */
export const CASCADING: OverlayEntry = {
  id: "Cascading",
  effects: [],
};

/**
 * **COMP/CON Engaged** (RAW Core p.71) — landed by the
 * `request_engage_compcon` protocol (0 action points) once the actor is proven to carry a
 * cascade-immune AI system — an equipped system tagged `tg_ai` + `tg_no_cascade`
 * (`packages/rules/src/encounter/economy.ts`'s `checkEngageCompConLegality`); the marker-only
 * step builder lives in `engageCompCon.ts`.
 *
 * **MARKER-ONLY**: the entry carries no clause. RAW's "the AI gains its own set of actions and
 * reactions" and "acts on the same turn as the pilot" is how the engine already runs the mech: the
 * player drives it through the normal controls on the mech's activation. What the marker changes
 * lives in the gathers: `combatantEffectIds` (`effects/loader.ts`) drops the carrier's talents and
 * core bonuses while it stands (Core p.106, "AIs don't benefit from the pilot's talents or other
 * features").
 * `expiry: { kind: "until_removed" }` — renders as a free status pill.
 */
export const COMPCON_ENGAGED: OverlayEntry = {
  id: "COMP/CON Engaged",
  effects: [],
};

/**
 * **Down and Out** (status) — a *pilot* reduced to 0 HP: unconscious, and RAW treats it as
 * Stunned. The can't-act wall gates on the literal `"Down and Out"` name ALONGSIDE
 * `isStunned()` (`checkActorStatusLegality`/`checkReactionStatusLegality`, `economy.ts`) — so
 * this status is hard-enforced the moment anything attaches it. Attached by
 * `resolveDamageCascade`'s null-structure branch (`tables.ts`) the first
 * time a `pilot`-kind combatant hits 0 HP; further damage while already Down and Out kills
 * it (a `destroy` step + the `"Dead"` marker, `tables.ts` again).
 *
 * **`effects` SPREADS `STUNNED`'s effects by object reference** (`...STUNNED.effects`, not a
 * copy) — the same `SHUT_DOWN` composite precedent (see its own doc comment below):
 * zero-drift, a Down-and-Out carrier inherits `evasionCap`/`autoFailChecks` automatically,
 * verified by `seedEntries.downAndOutSpread.test.ts`, which asserts identity, not just
 * deep-equality.
 */
export const DOWN_AND_OUT: OverlayEntry = {
  id: "Down and Out",
  effects: [
    ...STUNNED.effects,
  ],
};

/**
 * **Shut Down** (status) — a powered-off mech: it is STUNNED indefinitely, has IMMUNITY
 * to all tech actions/attacks (including from allied characters — no exceptions),
 * cleared heat/Exposed, and can take no actions until it BOOTS UP. **ONE composite
 * status, not a `Stunned` + `Shut Down` pair**: `status_add`/`status_remove` are idempotent
 * BY NAME, so stacking a second `Stunned` onto an already-Stunned mech would silently no-op
 * and let a foreign `save_ends` instance later expire the carrier out of Shut Down, a RAW
 * violation. Its `effects` **spread `STUNNED`'s effects by object reference** plus its own
 * immunity facet — zero-drift: the `evasionCap`/`autoFailChecks` facets live on
 * `STUNNED.effects[0].mod`, and because `SHUT_DOWN.effects` spreads that SAME array
 * (`...STUNNED.effects`, not a copy), a Shut Down carrier inherits both automatically —
 * verified by `seedEntries.stunnedSpread.test.ts`, which asserts identity, not just
 * deep-equality. See {@link isStunned} (`@lancer-vtt/domain`), the predicate a "can this
 * combatant act" gate must key off instead of the raw `"Stunned"` name.
 *
 * **The tech-immunity clause is HARD-ENFORCED**: the `immunity: [{kind:"tech"}]` facet (no
 * `except` — RAW blocks even Scan/Lock On/Bolster) folds into the tech-legality gate at
 * `encounterRoom.resolveIntent`, beside the Hidden/range gates, and hard-rejects a tech
 * attack/activate against a Shut Down carrier BEFORE dice (GM `gm_force` punches
 * through). The "can't act" half (it is STUNNED) is enforced separately, keyed off
 * `isStunned`/the overlay fold, never the raw name.
 */
export const SHUT_DOWN: OverlayEntry = {
  id: "Shut Down",
  effects: [
    ...STUNNED.effects,
    {
      kind: "modifier",
      mod: { immunity: [{ kind: "tech" }] },
    },
  ],
};

/**
 * **Biological** (status) — the RAW NPC tag (Core p. 282) for non-machine hostiles:
 * "immune to tech actions other than Scan and Lock On (including beneficial ones)".
 * Authored here as the `{kind:"tech", except:["act_scan","act_lock_on"]}` scope of the
 * SAME Immunity facet Shut Down carries — folded + hard-enforced at the identical
 * tech-legality gate, no separate mechanism. Attached at spawn for
 * `class.role === "biological"` NPCs; the unmounted-pilot Biological tag (Core p. 74)
 * inherits this same entry for free.
 */
export const BIOLOGICAL: OverlayEntry = {
  id: "Biological",
  effects: [
    {
      kind: "modifier",
      mod: {
        immunity: [{ kind: "tech", except: ["act_scan", "act_lock_on"] }],
      },
    },
  ],
};

/**
 * **Hidden** (status) — a HIDDEN character can't be targeted directly by attacks/hostile
 * actions (only by area effects); attacking or taking a hostile action clears it.
 *
 * The **"can't be targeted directly" rule is HARD-ENFORCED** — NOT here in the overlay, but
 * as a room legality gate beside the range gate (`encounterRoom`'s `resolveIntent`): a
 * unit-targeted attack on a Hidden combatant hard-rejects before any dice, the GM
 * `gm_force` override punches through, and area/cell attacks still resolve.
 *
 * **Reveal-on-attack is HARD-APPLIED** — also not a modifier here, but a post-attack
 * lifecycle observer the room fires after the resolution commits: a Hidden attacker that
 * makes an attack / forces a save has its Hidden status cleared as a recorded `status_remove`
 * step. The modifier below survives only as a resolve-time **play-by-play note** — the
 * warning that this attack reveals the Hidden attacker — surfaced alongside the real clear step.
 */
export const HIDDEN: OverlayEntry = {
  id: "Hidden",
  effects: [],
};

/**
 * **Invisible** (RAW Core p.77) — "all attacks against an INVISIBLE character have a 50%
 * chance to miss outright before the roll." A `miss_chance` replacement on the attack attempt: the
 * resolver draws ONE recorded coin (a d2) immediately before the d20, strictly after every
 * `needs_decision` early-out (`resolve/attackPreRoll.ts`) — auto-rolled, not GM-adjudicated. On the
 * losing face the attack misses with no d20 (a `miss_chance` step instead of `to_hit`) and runs the
 * miss path; on the winning face resolution proceeds normally. Applies per-target inside an area
 * fan-out too.
 * The remaining clause — "can always Hide, even without cover" — is HARD-ENFORCED:
 * `checkHideCover` (`hide.ts`) short-circuits to `ok` for an Invisible hider before the
 * per-hostile cover scan even runs.
 *
 * **Observer-relative** (`ll1-invade-options` ruling 5): an attacker whose `Tracking Bug` rides the
 * target ignores its Invisible ("you ignore their Invisible status", `ms_tracking_bug`), so the
 * clause is gated off when the target carries a bug the attacker planted.
 */
export const INVISIBLE: OverlayEntry = {
  id: "Invisible",
  effects: [
    {
      kind: "replacement",
      name: "Invisible",
      on: "attack_attempt",
      condition: { op: "not", pred: { op: "has_status", who: "target", status: "Tracking Bug", source: { actor: "attacker" } } },
      instead: { op: "miss_chance", chance: 0.5 },
    },
  ],
};

/**
 * **Intangible** (status) — an INTANGIBLE character ignores movement obstructions and
 * can't be targeted/affected by most attacks while intangible. The targeting rule needs
 * the same per-attack gate Invisible/Hidden have; advisory until then.
 */
export const INTANGIBLE: OverlayEntry = {
  id: "Intangible",
  effects: [
    {
      kind: "modifier",
      mod: {
        advisory:
          "carrier is Intangible — ignores movement obstructions and can't be targeted by most attacks (the GM enforces this; the attack resolves anyway)",
      },
    },
  ],
};

// ── tech actions (INVADE / LOCK ON / SCAN) ──────────────────────────────────────────
//
// The three core tech actions, composed from the existing spine (the roll + apply-a-status
// + the prompt menu):
//   - **INVADE** is the only one that is a tech *attack* (RAW): a `request_attack
//     { tech: true, weapon: "act_invade" }` rolls d20 + Tech Attack vs E-DEFENSE; on a
//     hit, this on-hit `triggered` effect deals 2 Heat + the FRAGMENT SIGNAL menu (a
//     `choose` — the menu-of-outcomes via the prompt transport — whose default option,
//     FRAGMENT SIGNAL, makes the target Impaired & Slowed until end of its next turn).
//     (Additional options come from the attacker's talents and systems: each authors an
//     `invade_option` clause, which `extend: "invade_options"` appends after FRAGMENT SIGNAL.)
//   - **LOCK ON** and **SCAN** are NOT tech attacks (no roll): Lock On applies the LOCK
//     ON condition to the target (an `action` whose effect is `add_status`); Scan is a
//     narrative info-gather (an `action` with no mechanical effect — advisory). Both ride
//     `request_activate` (the activate intent), not the attack spine.

/**
 * **INVADE** (`act_invade`) — the canonical tech attack. Fired as `request_attack
 * { tech: true, weapon: "act_invade" }`: the attack spine rolls d20 + Tech Attack vs the
 * target's E-DEFENSE (the `tech` flag), and on a hit this on-hit effect deals **2 Heat**
 * and offers the **invasion menu** (`choose`). FRAGMENT SIGNAL (the default / universal
 * option) makes the target **Impaired** & **Slowed** until the end of its next turn.
 */
export const INVADE: OverlayEntry = {
  id: "act_invade",
  effects: [
    {
      kind: "triggered",
      on: "attack",
      match: { family: "attack", hit: true },
      effect: [
        { op: "add_heat", amount: 2, target: "target" },
        {
          op: "choose",
          prompt: "invade_option",
          extend: "invade_options",
          options: [
            {
              id: "fragment_signal",
              label: "Fragment Signal — the target becomes Impaired & Slowed until the end of its next turn",
              effect: [
                { op: "add_status", status: "Impaired", until: { kind: "turn", edge: "end", who: "target" }, target: "target" },
                { op: "add_status", status: "Slowed", until: { kind: "turn", edge: "end", who: "target" }, target: "target" },
              ],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * **LOCK ON** (`act_lock_on`) — a quick tech action (NOT a tech attack — no roll). The
 * actor digitally marks a target within SENSORS + line of sight; the target gains the
 * **LOCK ON** condition (any attacker may later consume it for +1 Accuracy — the existing
 * Lock On modifier). An `action` whose effect lands the status on the bound `target`.
 * `until_removed` — Lock On persists until consumed or the target SHUTS DOWN (the consume
 * is the Lock On modifier's `consumeOnHit`; the shutdown clear is advisory).
 */
export const LOCK_ON_ACTION: OverlayEntry = {
  id: "act_lock_on",
  effects: [
    {
      kind: "action",
      activation: "quick",
      effect: [{ op: "add_status", status: "Lock On", until: { kind: "until_removed" }, target: "target" }],
    },
  ],
};

/**
 * **BOLSTER** (`act_bolster`) — a quick tech action (NOT a tech attack — no roll). The actor
 * uses its mech's processing power to enhance an ally: choose a character within SENSORS (the
 * room Sensors-gates it via `checkTechRange`, exactly like INVADE — RAW Bolster needs only
 * SENSORS, no line of sight, unlike SCAN). The target gains the **Bolster** buff (+2◆ on its
 * next save or skill check). An `action` whose effect lands the status on the bound `target`,
 * lasting `target_next_turn_end` — "between now and the end of their next turn" (RAW Core
 * p. 69) — consumed earlier by the Bolster modifier's `consumeOnSaveCheck` if the target makes
 * a save/check first.
 */
export const BOLSTER_ACTION: OverlayEntry = {
  id: "act_bolster",
  effects: [
    {
      kind: "action",
      activation: "quick",
      effect: [{ op: "add_status", status: "Bolster", until: { kind: "turn", edge: "end", who: "target" }, target: "target" }],
    },
  ],
};

/**
 * **SCAN** (`act_scan`) — a quick tech action (NOT a tech attack — no roll). RAW (Core
 * p.64/283) offers the scanning player ONE of three buckets: (a) weapons/systems/full
 * statistics, (b) one piece of hidden information, (c) generic/public records. **This
 * mechanizes bucket (a) only**: a successful Scan sets the target's `disclosure = "full"` — a
 * REAL, undoable state change (the `reveal` atom, mirroring the GM's manual `set_disclosure`
 * control, converging on the same `withDisclosureLevel` reducer helper). Buckets (b)/(c) have
 * no modelable state and stay a GM conversation (a player wanting one asks the GM out of
 * character) — so this slightly over-grants (a) against a strict "pick one" reading, which is
 * accepted. Scanning a non-hostile (ally/own unit) is an inert no-op in practice (already
 * fully visible; nothing to reveal) — the atom no-ops cleanly since `disclosure` on an
 * already-`"full"` combatant emits no step.
 */
export const SCAN: OverlayEntry = {
  id: "act_scan",
  effects: [
    {
      kind: "action",
      activation: "quick",
      // The reveal: sets the target's disclosure to "full" (bucket (a)). Omitted `target`
      // defaults to the resolution's primary target (the scanned combatant).
      effect: [{ op: "reveal", level: "full" }],
    },
  ],
};

/**
 * **`fx_turn_start_demo`** — NOT real Lancer content; engine test scaffolding for the
 * coverage machinery's "mechanized-by-vocabulary is not the same as reachable/gathered"
 * distinction.
 *
 * `turn_start` fires through `fireTrigger` for real (`resolve.ts`'s `resolveLifecycleTrigger`,
 * fired by the room's `applyActivation` hook chain), so this fixture's trigger point is not
 * inert and `inertTriggeredClauses` correctly stops reporting it. It still has **zero effect
 * on resolution**: no equipment/status/pilot-effect id references `fx_turn_start_demo`, so
 * `resolveLifecycleTrigger` never finds it in any carrier's gather. Every atom it uses is
 * implemented, so `coverage.ts`'s tier classifier calls it `mechanized`.
 *
 * **It deliberately STAYS here rather than moving to a pack**: a sidecar pack ships real (or
 * synthetic-but-realistic) rules content, and this is neither. Do not migrate it and do not
 * delete it.
 */
export const TURN_START_FIXTURE: OverlayEntry = {
  id: "fx_turn_start_demo",
  effects: [
    {
      kind: "triggered",
      on: "start_turn",
      effect: [{ op: "add_status", status: "Impaired", target: "self" }],
    },
  ],
};

// ── parameterized tags (`{VAL}` templates, bound per-weapon at gather) ──────────────
//
// A tag definition is a TEMPLATE whose `{VAL}` placeholder binds from the weapon's
// `TagRef.val` at attach. AP/Overkill carry no value (booleans); Accurate/Reliable
// /Limited/Knockback are `{VAL}`-parameterized. Knockback is positional (a forced move,
// deferred to the spatial layer — modeled as a no-op `tag` rider carrying its bound val
// so it's recorded, not a faked grounded step). Reliable's mechanism (min damage on a
// hit or a miss) is already in the frozen `WeaponProfile.reliable` flatten — the tag template is
// the authored *source* the loader could bind, kept here for the `{VAL}` proof.

/**
 * **AP** (`tg_ap`) — armor-piercing. A flag tag (no `{VAL}`). The armor-ignore
 * *mechanism* rides the frozen `WeaponProfile.ap` on the damage path, so the
 * authored tag is a recorded `tag` rider (like Overkill) — the authored *source* of the
 * frozen flag, not a second armor-ignore that would double-apply.
 */
export const TAG_AP: OverlayEntry = {
  id: "tg_ap",
  effects: [{ kind: "modifier", mod: { tag: { name: "ap" } } }],
};

/** **Accurate {VAL}** (`tg_accurate`) — +{VAL} accuracy to the carrier's attacks (the `{VAL}` proof). */
export const TAG_ACCURATE: OverlayEntry = {
  id: "tg_accurate",
  // `attackerAccuracy: "{VAL}"` — the loader binds it from the weapon's tg_accurate val.
  effects: [{ kind: "modifier", mod: { attackerAccuracy: VAL } }],
};

/** **Reliable {VAL}** (`tg_reliable`) — min {VAL} damage on a hit or a miss (a `tag` rider carrying the bound val). */
export const TAG_RELIABLE: OverlayEntry = {
  id: "tg_reliable",
  effects: [{ kind: "modifier", mod: { tag: { name: "reliable", val: VAL } } }],
};

/** **Overkill** (`tg_overkill`) — reroll 1s, heat to wielder. A flag tag (the frozen profile carries the mechanism). */
export const TAG_OVERKILL: OverlayEntry = {
  id: "tg_overkill",
  effects: [{ kind: "modifier", mod: { tag: { name: "overkill" } } }],
};

/** **Limited {VAL}** (`tg_limited`) — N uses before a Full Repair (a resource; the `{VAL}` cap rides the tag rider). */
export const TAG_LIMITED: OverlayEntry = {
  id: "tg_limited",
  effects: [{ kind: "modifier", mod: { tag: { name: "limited", val: VAL } } }],
};

/**
 * **Knockback {VAL}** (`tg_knockback`) — an involuntary positional push of {VAL} spaces on
 * hit. Two clauses:
 *   1. the recorded `tag` rider modifier (carries the bound `{VAL}` into the step list /
 *      coverage — the auditable provenance), and
 *   2. a **triggered `on_hit`** clause emitting a `forced_move` of `dist {VAL}` away from
 *      the attacker — the *positional consequence*, MECHANIZED. The destination cell is the
 *      spatial answerer's job (the interpreter queries `@lancer-vtt/space`: push along the
 *      hex line away from the attacker, stop at the map edge / a blocker). With no map the
 *      step records `to: null` (an honest no-op) and surfaces a deferral — never a faked push.
 */
export const TAG_KNOCKBACK: OverlayEntry = {
  id: "tg_knockback",
  effects: [
    { kind: "modifier", mod: { tag: { name: "knockback", val: VAL } } },
    {
      kind: "triggered",
      on: "attack",
      match: { family: "attack", hit: true },
      effect: [{ op: "forced_move", dist: VAL, target: "target", away_from: "attacker" }],
    },
  ],
};

/**
 * **RAM** (`act_ram`) — a melee **control action** resolved through the attack spine (the
 * `act_invade` pattern, melee analogue). Fired as `request_attack { weapon: "act_ram" }`:
 * the spine rolls d20 + attack bonus (Grit) vs the target's **Evasion** (a melee attack —
 * non-tech, so no E-DEFENSE) and emits **no weapon damage** (Ram's consequence is positional/
 * status, not damage — the spine skips the damage block for an action attack). Two RAW gates
 * live *before* the roll, off this overlay: adjacency (Ram's synthetic threat-1 reach, via the
 * shared `checkAttackRange`) and Size (`checkRamSize` — "the same SIZE or smaller").
 *
 * On a **hit** (RAW p.70: "your target is knocked PRONE and you may also choose to knock them
 * back by one space, directly away from you"):
 *   - **Prone is automatic** — `add_status: Prone` with NO `force_save` (unlike Power Knuckles /
 *     Thresher Maul, which gate Prone behind a Hull save; Ram grants the target no save). Prone
 *     lands as the catalog's own entry, which carries the real +1-Difficulty / SLOWED /
 *     difficult-terrain math.
 *   - **The 1-space push is the attacker's option** — a `choose` round-trip (the established
 *     `choose`/`needs_decision` transport, Custom Paint Job's pattern) whose **default first**
 *     option is *no push* (RAW "may also choose" → off unless chosen). The `push` branch emits
 *     a `forced_move dist:1 away_from:attacker` (the real Knockback primitive — `pushDestination`
 *     walks the hex line away, stops at the map edge / a blocker). The CLI opts in via `--push`.
 */
export const RAM: OverlayEntry = {
  id: "act_ram",
  effects: [
    {
      kind: "triggered",
      on: "attack",
      match: { family: "attack", hit: true },
      effect: [
        // Automatic, no save (RAW Ram grants none) — the catalog's Prone entry carries the math.
        { op: "add_status", status: "Prone", until: { kind: "turn", edge: "end", who: "target" }, target: "target" },
        // The optional 1-space knockback. The FIRST option (no push) is the recorded default,
        // so a Ram with no `--push` choice resolves to Prone only ("may also choose" → off).
        {
          op: "choose",
          prompt: "ram_push",
          options: [
            { id: "no_push", label: "Don't knock back", effect: [] },
            {
              id: "push",
              label: "Knock back 1 space, directly away",
              effect: [{ op: "forced_move", dist: 1, target: "target", away_from: "attacker" }],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * **GRAPPLE** (`act_grapple`) — a melee **control
 * action** resolved through the attack spine, the exact `RAM` shape minus its Size gate and
 * damage: fired as `request_attack { weapon: "act_grapple" }`, the spine rolls d20 + attack
 * bonus vs the target's **Evasion** (a melee attack — no contest on the hit itself) and emits
 * **no weapon damage**. **No Size gate** — Ram-only, Grapple RAW has none.
 *
 * On a **hit** (RAW p.72: "both characters become ENGAGED"), **bind both**: an invented
 * **`Grappled`** overlay marker on each, mutually **partner-stamped** via the `partner` atom
 * field (`target`'s marker carries `partner: "attacker"`, `attacker`'s carries
 * `partner: "target"` — each side points at the other), plus real **`Engaged`** on both (the
 * `ENGAGED` overlay entry supplies the real `attackerAccuracy: -1` math the moment the status
 * lands — no extra wiring needed here). `Grappled`'s riders (can't-Boost/react,
 * Immobilized-on-the-smaller, the mirror-movement drag) live on the
 * `GRAPPLED` entry above. The grapple ends via
 * `request_grapple_break` (contested-HULL Quick action), `request_grapple_release` (attacker
 * free action, forced or not), or the broken-adjacency post-move recomputer
 * (`spatialRecompute.ts`) — none of those live on this overlay entry.
 */
export const GRAPPLE: OverlayEntry = {
  id: "act_grapple",
  effects: [
    {
      kind: "triggered",
      on: "attack",
      match: { family: "attack", hit: true },
      effect: [
        // The mutual bind: each side's Grappled marker points at the other.
        { op: "add_status", status: "Grappled", until: { kind: "until_removed" }, target: "target", partner: "attacker" },
        { op: "add_status", status: "Grappled", until: { kind: "until_removed" }, target: "attacker", partner: "target" },
        // Real Engaged on both — free and faithful (ENGAGED already carries the real math).
        { op: "add_status", status: "Engaged", until: { kind: "until_removed" }, target: "target" },
        { op: "add_status", status: "Engaged", until: { kind: "until_removed" }, target: "attacker" },
      ],
    },
  ],
};

/**
 * Every authored seed entry (the loader's input). Order is merge order (last id
 * wins on a clash; there are none here). The loader keys these by `id` into the live
 * overlay; tag templates keep their `{VAL}` placeholders (bound per-weapon at gather).
 */
export const SEED_ENTRIES: readonly OverlayEntry[] = [
  IMPAIRED,
  LOCK_ON,
  BOLSTER,
  SHREDDED,
  EXPOSED,
  BRACED,
  STUNNED,
  IMMOBILIZED,
  FLYING,
  GRAPPLED,
  SLOWED,
  PRONE,
  JAMMED,
  ENGAGED,
  DANGER_ZONE,
  CASCADING,
  COMPCON_ENGAGED,
  DOWN_AND_OUT,
  SHUT_DOWN,
  BIOLOGICAL,
  HIDDEN,
  INVISIBLE,
  INTANGIBLE,
  INVADE,
  RAM,
  GRAPPLE,
  LOCK_ON_ACTION,
  BOLSTER_ACTION,
  SCAN,
  TURN_START_FIXTURE,
  TAG_AP,
  TAG_ACCURATE,
  TAG_RELIABLE,
  TAG_OVERKILL,
  TAG_LIMITED,
  TAG_KNOCKBACK,
];
